<template>
  <wa-toast :placement="resolvedPlacement" class="ui-toast-host" data-cy="toast-host">
    <wa-toast-item
      v-for="toast in toasts"
      :key="toast.id"
      :variant="VARIANT[toast.severity]"
      :duration="toast.life || 0"
      :class="{ 'ui-toast-item--no-close': !toast.closable }"
      :data-severity="toast.severity"
      :data-cy="`toast-${toast.severity}`"
      @wa-after-hide.self="remove(toast.id)"
    >
      <span slot="icon" class="ui-toast__icon">
        <font-awesome-icon :icon="ICON[toast.severity]" />
      </span>
      <div class="ui-toast__body tw:flex tw:flex-col tw:gap-2">
        <div v-if="toast.summary" class="ui-toast__summary tw:text-base">{{ toast.summary }}</div>
        <div v-if="toast.detail" class="ui-toast__detail tw:text-sm">{{ toast.detail }}</div>
      </div>
    </wa-toast-item>
  </wa-toast>
</template>

<script setup lang="ts">
/**
 * Renders the shared toast queue (components/ui/toast-queue.ts) with Web Awesome's
 * `<wa-toast>` stack + `<wa-toast-item>`s. Mounted ONCE, in `app.vue`, so every layout
 * (portal, guest-flow, default, embedded, …) shows toasts. Items are declared from Vue
 * state (not `wa-toast.create()`) so we can slot a FontAwesome icon and structured
 * summary/detail content.
 *
 * Placement defaults to `top-end` on desktop and `bottom-center` under 768px (the old
 * per-layout `isMobile` rule); pass `placement` to pin it.
 *
 * Lifecycle: <wa-toast> starts each slotted item's timer on slotchange; an item hides on
 * timeout or close-click, fires `wa-after-hide`, and removes itself from the DOM — we then
 * drop it from the queue so Vue's keyed list stays in sync. `life` undefined/0 → sticky
 * (PrimeVue semantics; Web Awesome `duration="0"`). `closable: false` has no Web Awesome
 * attribute, so it is a class the theme CSS uses to hide the close-button part.
 */
import '@awesome.me/webawesome/dist/components/toast/toast.js'
import '@awesome.me/webawesome/dist/components/toast-item/toast-item.js'

import { computed, onMounted, onUnmounted, ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faCheckCircle, faInfoCircle, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { useToast, type ToastSeverity } from '~/composables/useToast'
import { toastQueue } from '~/components/ui/toast-queue'

export type UiToastPlacement =
  | 'top-start' | 'top-center' | 'top-end'
  | 'bottom-start' | 'bottom-center' | 'bottom-end'

const MOBILE_BREAKPOINT = 768

const VARIANT: Record<ToastSeverity, string> = { success: 'success', error: 'danger', warn: 'warning', info: 'neutral' }
const ICON: Record<ToastSeverity, IconDefinition> = {
  success: faCheckCircle,
  error: faTriangleExclamation,
  warn: faTriangleExclamation,
  info: faInfoCircle,
}

const props = defineProps<{ placement?: UiToastPlacement }>()

const toasts = toastQueue
const { remove } = useToast()

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : Infinity)
const onResize = () => { windowWidth.value = window.innerWidth }
onMounted(() => window.addEventListener('resize', onResize))
onUnmounted(() => window.removeEventListener('resize', onResize))

const resolvedPlacement = computed<UiToastPlacement>(
  () => props.placement ?? (windowWidth.value < MOBILE_BREAKPOINT ? 'bottom-center' : 'top-end'),
)
</script>
